import { useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Apple, ShieldCheck, KeyRound, Fingerprint, Landmark, Eye } from "lucide-react";
import securityHero from "@/assets/security-hero.jpg";

interface SecurityTopic {
  id: string;
  icon: React.ElementType;
  label: string;
  gradient: string;
  title: string;
  description: string;
}

const topics: SecurityTopic[] = [
  {
    id: "apple",
    icon: Apple,
    label: "Écosystème Apple",
    gradient: "from-slate-500 to-slate-700",
    title: "Partenariat & Écosystème Apple",
    description:
      "Grafton a été conçu avec la conviction que la sécurité commence par le matériel. C'est pourquoi nous développons en priorité sur l'écosystème Apple.\n\n• **Secure Enclave** — Les clés de chiffrement sont stockées dans la puce Apple dédiée, inaccessibles au système d'exploitation.\n• **App Sandbox & Gatekeeper** — Chaque processus est isolé, empêchant tout accès non autorisé à vos données.\n• **FileVault & Data Protection** — Vos fichiers sont chiffrés au repos avec AES-256.\n• **Politique de confidentialité Apple** — Aucune monétisation de vos données personnelles.\n\nNotre ambition : un partenariat stratégique avec Apple pour offrir le plus haut niveau de confiance à nos utilisateurs.",
  },
  {
    id: "data",
    icon: ShieldCheck,
    label: "Protection des données",
    gradient: "from-amber-500 to-orange-600",
    title: "Protection avancée de vos données",
    description:
      "Vos données sont notre responsabilité la plus précieuse. Grafton implémente plusieurs couches de défense :\n\n• **Chiffrement de bout en bout (E2EE)** — Les données sensibles sont chiffrées avant même de quitter votre appareil.\n• **Chiffrement au repos (AES-256)** — Toutes les données stockées sont protégées par un chiffrement de grade militaire.\n• **Chiffrement en transit (TLS 1.3)** — Chaque communication est sécurisée par le protocole le plus récent.\n• **Row-Level Security (RLS)** — Chaque utilisateur n'accède qu'à ses propres données, garanti au niveau base de données.\n• **Isolation des environnements** — Les données de production sont strictement séparées des environnements de test.\n• **Journalisation & audit trail** — Chaque accès et modification est tracé et horodaté.\n• **Conformité RGPD** — Droit d'accès, de rectification et de suppression de vos données à tout moment.",
  },
  {
    id: "approval",
    icon: KeyRound,
    label: "Validation dirigeant",
    gradient: "from-blue-500 to-indigo-600",
    title: "Validation systématique du dirigeant",
    description:
      "Comme dans le système bancaire, les actions critiques dans Grafton requièrent l'approbation explicite du dirigeant :\n\n• **Actions stratégiques** — Toute décision impactant la structure de l'entreprise nécessite une validation.\n• **Modifications de paramètres sensibles** — Changement de coordonnées bancaires, ajout d'utilisateurs, modification des accès.\n• **Suppression de données** — Aucune donnée critique ne peut être supprimée sans confirmation du dirigeant.\n• **Notifications en temps réel** — Le dirigeant est immédiatement alerté de toute action en attente de validation.\n\nVous gardez le contrôle total de votre entreprise.",
  },
  {
    id: "2fa",
    icon: Fingerprint,
    label: "Double vérification",
    gradient: "from-emerald-500 to-teal-600",
    title: "Double vérification financière",
    description:
      "Toutes les actions financières sont soumises à une authentification renforcée à deux facteurs (2FA) :\n\n• **Authentification biométrique** — Face ID ou Touch ID sur les appareils Apple compatibles.\n• **Code OTP temporaire** — Un code à usage unique envoyé par SMS ou application d'authentification.\n• **Validation en deux étapes** — Initiation puis confirmation sur un appareil séparé.\n• **Seuils de sécurité** — Les transactions au-delà d'un certain montant déclenchent automatiquement une vérification supplémentaire.\n• **Historique des validations** — Traçabilité complète de chaque action financière approuvée.",
  },
  {
    id: "treasury",
    icon: Landmark,
    label: "Veille trésorerie",
    gradient: "from-violet-500 to-purple-600",
    title: "Veille intelligente de trésorerie",
    description:
      "Grafton surveille en permanence la santé financière de votre organisation :\n\n• **Alertes prédictives** — L'IA anticipe les tensions de trésorerie avant qu'elles ne surviennent.\n• **Seuils d'alerte personnalisés** — Définissez vos propres niveaux d'alerte (solde minimum, dépenses inhabituelles).\n• **Rapports automatiques** — Synthèse quotidienne et hebdomadaire de votre situation financière.\n• **Détection d'anomalies** — Identification automatique des flux atypiques ou des écarts budgétaires.\n• **Recommandations IA** — Suggestions d'optimisation de votre trésorerie basées sur vos données historiques.\n\nVotre sérénité financière, notre mission.",
  },
  {
    id: "monitoring",
    icon: Eye,
    label: "Monitoring 24/7",
    gradient: "from-rose-500 to-pink-600",
    title: "Surveillance continue 24/7",
    description:
      "L'infrastructure Grafton est surveillée en permanence :\n\n• **Monitoring temps réel** — Surveillance continue de la disponibilité, des performances et de la sécurité.\n• **Détection d'intrusion (IDS)** — Analyse comportementale pour identifier toute activité suspecte.\n• **Sauvegardes automatiques** — Vos données sont sauvegardées toutes les 6 heures avec rétention de 30 jours.\n• **Plan de reprise d'activité (PRA)** — En cas d'incident, restauration complète en moins de 4 heures.\n• **Tests de pénétration** — Audits de sécurité réguliers par des experts indépendants.",
  },
];

export default function SecurityPage() {
  const [openTopic, setOpenTopic] = useState<string | null>(null);
  const current = topics.find((t) => t.id === openTopic);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <header className="h-12 flex items-center border-b border-border/40 px-4">
            <SidebarTrigger />
            <h1 className="ml-3 text-sm font-semibold text-foreground">Sécurité</h1>
          </header>

          <main className="flex-1 overflow-y-auto">
            {/* Hero */}
            <div className="relative w-full h-56 md:h-72 overflow-hidden">
              <img
                src={securityHero}
                alt="Sécurité Grafton IA"
                className="w-full h-full object-cover"
                width={1920}
                height={640}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              <div className="absolute bottom-6 left-6 md:left-10">
                <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-1">
                  Grafton IA
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                  Sécurité & Confidentialité
                </h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  Votre entreprise mérite le même niveau de protection que les institutions financières.
                </p>
              </div>
            </div>

            {/* Topic buttons */}
            <div className="px-4 md:px-10 py-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => setOpenTopic(topic.id)}
                    className="group flex flex-col items-center gap-3 p-5 rounded-2xl border border-border/40 bg-card hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
                  >
                    <div
                      className={`h-14 w-14 rounded-xl bg-gradient-to-br ${topic.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <topic.icon className="h-7 w-7 text-white" />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground text-center leading-tight group-hover:text-foreground transition-colors">
                      {topic.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Trust bar */}
            <div className="px-4 md:px-10 pb-10">
              <div className="rounded-2xl border border-border/40 bg-card/50 p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                <ShieldCheck className="h-8 w-8 text-amber-500 shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">
                    Engagement de confiance
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Grafton IA s'engage à ne jamais vendre, partager ou exploiter vos données à des fins
                    commerciales. Nous appliquons les normes les plus strictes inspirées du secteur bancaire
                    et de l'écosystème Apple pour garantir la sécurité de votre organisation.
                  </p>
                </div>
              </div>
            </div>
          </main>

          {/* Dialog */}
          <Dialog open={!!openTopic} onOpenChange={() => setOpenTopic(null)}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  {current && (
                    <div
                      className={`h-10 w-10 rounded-lg bg-gradient-to-br ${current.gradient} flex items-center justify-center`}
                    >
                      <current.icon className="h-5 w-5 text-white" />
                    </div>
                  )}
                  <DialogTitle>{current?.title}</DialogTitle>
                </div>
                <DialogDescription asChild>
                  <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line prose prose-sm prose-invert max-w-none">
                    {current?.description.split("\n").map((line, i) => {
                      const bold = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
                      return (
                        <p key={i} dangerouslySetInnerHTML={{ __html: bold }} className="my-1" />
                      );
                    })}
                  </div>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </SidebarProvider>
  );
}
